<template>
  <div class="unlock-chart-prompt text-center" style="border-radius: 0 0 10px 20px">
    <span class="icon-small-lock" style="font-size: 30px"></span>
    <h4 class="mt-3">
      <span v-if="isCelebrity">
        {{ $t("billing.unlockTypes.celebrity") }}
      </span>
      <span v-else-if="category !== 'transit_tool'">
        {{ $t("billing.unlock") }} {{ $t("billing.unlockTypes." + category) }}
      </span>
      <!-- special case for transit tool title -->
      <span v-else>
        {{ $t("billing.unlockTypes." + category) }}
      </span>
    </h4>
    <div class="mbg-text-muted px-3">
      <div
        v-if="category === 'transit_tool'"
        v-html="$t('billing.unlockTransitToolSubtitle')"
      ></div>
      <p v-else-if="isCelebrity" class="w-75 mx-auto">
        {{ $t("billing.unlockCelebrityChartSubtitle") }}
      </p>
      <p v-else>
        {{ $t("billing.unlockChartSubTitles." + category) }}
      </p>
      {{
        DISCOUNTS_ENABLED[category]
          ? $t("billing.unlockChartDiscount", {
              percentage: DISCOUNT_PERCENTAGE[category],
            })
          : ""
      }}
    </div>

    <b-button variant="unlock-chart" @click="unlockChart" :disabled="!!comingSoonMode">
      <span v-if="comingSoonMode">
        {{ $t("billing.comingSoonButton") }}
      </span>
      <span v-else-if="isCelebrity">
        {{ $t("billing.unlockCelebrityChartBtn") }}
      </span>
      <span v-else-if="category !== 'transit_tool'">
        {{ $t("billing.unlockChartButton") }}
      </span>
      <span v-else>
        {{ $t("billing.unlockTransitChartButton") }}
      </span>
    </b-button>

    <div v-if="showModal" class="unlock-chart-modal">
      <div
        class="unlock-chart-modal-backdrop backdrop-visible"
        @click.self="showModal = false"
      ></div>
      <div class="unlock-chart-modal-content-wrapper" @click.self="showModal = false">
        <div class="unlock-chart-modal-content" @click.self="showModal = false">
          <div class="pricing-modal-grid">
            <!-- Single Unlock Card -->
            <pricing-card
              :product="singleProductAdapter"
              :disabled="false"
              :custom-short-description-key="`pricing.modals.single.${category}.short`"
              :custom-description-key="`pricing.modals.single.${category}.long`"
              :custom-button="true"
              :available-intervals="[]"
            >
              <template #custom-button>
                <button class="pricing-card__button w-100" @click="unlockChartSingle">
                  {{ $t("billing.unlockChartButton") }}
                </button>
              </template>
            </pricing-card>

            <!-- Subscription Card -->
            <pricing-card
              :product="currentProducts.subscription"
              :disabled="hasMultipleSubscriptions"
              :custom-short-description-key="`pricing.modals.subscription.${currentProducts.subscription.key}.short`"
              :disabled-reason="
                hasMultipleSubscriptions
                  ? $t('billing.subscriptions.multipleLegacySubscriptionsNotice')
                  : null
              "
              :custom-description-key="`pricing.modals.subscription.${currentProducts.subscription.key}.long`"
              :available-intervals="subscriptionIntervals"
              :default-interval="0"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import "@/assets/styles/globals/_variables.scss";

.unlock-chart-prompt {
  padding: 20px;
}

.unlock-chart-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;

  .unlock-chart-modal-backdrop {
    z-index: 10000;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    transition: visibility 0.3s linear, opacity 0.3s linear;
    visibility: hidden;
    opacity: 0;
    backdrop-filter: blur(5px);

    &.backdrop-visible {
      visibility: visible;
      opacity: 1;
    }
  }
}

.unlock-chart-modal-content-wrapper {
  position: relative;
  z-index: 10001;
  height: 100%;
  overflow-y: auto;

  .unlock-chart-modal-content {
    min-height: 100%;
    position: relative;
    padding: 20px;

    @media (min-width: 767px) {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
    }
  }
}

.pricing-modal-grid {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1.5rem;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  :deep(.pricing-card__description) {
    text-align: left !important;

    p,
    div {
      text-align: left !important;
    }
  }
}
</style>

<script>
import { mapState } from "vuex";
import { createSingleCartItem } from "../../helpers/checkout_helper";
import { DISCOUNT_PERCENTAGE, DISCOUNTS_ENABLED } from "../../helpers/discounts";
import ServerContentMixin from "@/mixins/ServerContentMixin";
import StripeMixin from "@/mixins/StripeMixin";
import PricingCard from "./cart/PricingCard.vue";

export default {
  components: { PricingCard },
  props: {
    category: String, // foundational, advanced, companionship
    chartId: String, // chart id to redirect to!,
    comingSoonMode: Boolean,
  },
  mixins: [ServerContentMixin, StripeMixin],

  computed: {
    ...mapState({
      chart: (state) => state.charts.chart,
      products: (state) => state.checkout.products,
      subscriptions: (state) => state.account.subscriptions,
    }),

    hasMultipleSubscriptions() {
      return (
        this.subscriptions
          .filter((x) => x.status === 1)
          .map((x) => x.plans)
          .flat().length > 1
      );
    },
    isCelebrity() {
      return this.chart.isCelebrity;
    },
    DISCOUNT_PERCENTAGE() {
      return DISCOUNT_PERCENTAGE;
    },
    DISCOUNTS_ENABLED() {
      return DISCOUNTS_ENABLED;
    },

    currentProducts() {
      const single_unlock = this.products.singles.find((x) => x.key === this.category);

      const subscription =
        this.products.subscriptions.find((x) => x.oldKey === this.category) ||
        this.products.subscriptions.find((x) => x.key === this.category);

      return {
        single: single_unlock,
        subscription,
      };
    },

    singleProductAdapter() {
      return {
        key: this.category,
        name: this.$t("billing.unlockTypes." + this.category),
        color: "#c25354", // Primary color
        intervals: {
          0: {
            price: this.currentProducts.single.price,
            stripePriceId: null,
          },
        },
      };
    },

    subscriptionIntervals() {
      if (!this.currentProducts.subscription?.intervals) return [0];
      const intervals = Object.keys(this.currentProducts.subscription.intervals)
        .map((k) => parseInt(k))
        .filter((k) => this.currentProducts.subscription.intervals[k] !== undefined);
      // Order: Quarterly (2) first, Yearly (0) second
      return intervals.sort((a, b) => {
        const order = [2, 0];
        return order.indexOf(a) - order.indexOf(b);
      });
    },
  },

  data() {
    return {
      showModal: false,
    };
  },
  methods: {
    calculateDiscount(price) {
      return ((price * (100 - DISCOUNT_PERCENTAGE[this.category])) / 100).toFixed(0);
    },
    unlockChartSingle() {
      // Single unlock doesn't use Stripe checkout, goes to checkout page instead
      this.$store.commit("checkout/setCheckoutType", this.CheckoutTypes.SINGLE);
      this.$store.commit(
        "checkout/addSingleCartItem",
        createSingleCartItem(
          {
            id: this.$store.state.charts.chart.id,
            name: this.$store.state.charts.chart.meta.name,
          },
          this.currentProducts.single
        )
      );
      this.$router.push({ name: "checkout" });
    },

    unlockChart() {
      // if (this.comingSoonMode) return;
      if (this.isCelebrity || this.category === "transit_tool") {
        this.$router.push("/checkout");
      } else {
        this.showModal = true;
      }
    },
  },
};
</script>
